<template>
  <async-form
    @submit="onSubmit"
    @reset="$emit('reset')"
    v-if="formData"
    :name="schema.name"
    :enctype="schema.enctype"
    :autocomplete="schema.autocomplete"
    :accept-charset="schema['accept-charset']"
    :action="schema.action"
    :method="schema.method"
    :form-data="formData"
    :class="[baseCss]">
    <card :elevation="schema.elevation" :rounded="rounded" :overflow-off="false" :bg-color="bgColor" :bg-color-lightness="bgColorLightness">
      <template v-if="title">
        <card-title
          v-html="title"
          :bg-color="titleBgColor"
          :bg-color-lightness="titleBgColorLightness"
          :color="titleColor"
          :color-lightness="titleColorLightness" />
      </template>
      <card-text>
        <flex>
          <flex-col equal-height :span="field.span || 24" v-for="(field, index) in schema.fields" :key="index" sm12 xs24>
            <container :class="[dense ? 'p-1' : 'p-2']">
              <text-field
                v-if="['text-field', 'password'].includes(field.type)"
                v-model="formData[field.name].value"
                :type="textFields[field.type]"
                :color="color"
                :color-lightness="colorLightness"
                :label-color="labelColor"
                :label-color-lightness="labelColorLightness"
                :label="field.label"
                :reveal="field.reveal"
                :placeholder="field.placeholder"
                :disabled="field.disabled"
                :readonly="field.readonly"
                :required="field.required"
                :multiline="field.multiline"
                :cols="field.cols"
                :rows="field.rows"
                :hint="field.required ? field.invalid ? '&nbsp;' : field.validation.message || labelError : null"
                :hint-color="labelErrorColor"
                :hint-color-lightness="labelsColorLightness"
                :icon-left="field['icon-left']"
                :icon-right="field['icon-right']" />
              <dropdown
                v-if="field.type === 'dropdown'"
                v-model="formData[field.name].value"
                :color="color" :hint="field.required ? field.invalid ? '&nbsp;' : field.validation.message || labelError : null"
                :hint-color="labelErrorColor"
                :hint-color-lightness="labelsColorLightness"
                :color-lightness="colorLightness"
                :label-color="labelColor"
                :label-color-lightness="labelColorLightness"
                :disabled="field.disabled"
                :required="field.required"
                :label="field.label"
                :icon="field.icon"
                :multiple="field.multiple"
                :items="field.items" />
              <template v-if="['checkbox', 'switcher'].includes(field.type)">
                <checkbox
                  v-if="field.type === 'checkbox'"
                  v-model="formData[field.name].value"
                  :icon-color="color"
                  :icon-color-lightness="colorLightness"
                  :label-color="labelColor"
                  :label-color-lightness="labelColorLightness"
                  :disabled="field.disabled"
                  :label="field.label" />
                <switcher
                  v-if="field.type === 'switcher'"
                  v-model="formData[field.name].value"
                  :label-color="labelColor"
                  :label-color-lightness="labelColorLightness"
                  :bg-color="color"
                  :bg-color-lightness="colorLightness"
                  :disabled="field.disabled"
                  :label="field.label" />
                <text-field-label
                  v-if="field.required"
                  align="right"
                  :text="field.required ? field.invalid ? '&nbsp;' : field.validation.message || labelError : null"
                  :color="labelErrorColor"
                  :color-lightness="labelsColorLightness" />
              </template>
              <container v-if="field.type === 'color-picker'" :class="[`text-${field.align || 'left'}`]">
                <form-schema-label
                  :required-color="labelErrorColor"
                  :required-color-lightness="labelColorLightness"
                  :field="field"
                  :label-color="labelColor"
                  :label-color-lightness="labelColorLightness" />
                <color-picker
                  v-if="field.type === 'color-picker'"
                  v-model="formData[field.name].value"
                  :label="field.labelValue"
                  :label-size="field.labelValueSize"
                  :size="field.size"
                  :tile="field.tile"
                  :disabled="field.disabled"
                  :icon="field.icon"
                  :icon-size="field.iconSize"
                  :elevation="field.elevation"
                  :icon-color="field.iconColor"
                  :icon-color-lightness="field.iconColorLightness" />
              </container>
              <container v-if="field.type === 'rating'" :class="[`text-${field.align || 'left'}`]">
                <form-schema-label
                  :required-color="labelErrorColor"
                  :required-color-lightness="labelColorLightness"
                  :field="field" :label-color="labelColor"
                  :label-color-lightness="labelColorLightness" />
                <rating
                  @input="onRatingInput($event, index)"
                  :items="field.items"
                  :disabled="field.disabled"
                  :name="field.name"
                  :value="field.value"
                  :icon="field.icon"
                  :color="field.color"
                  :color-lightness="field['color-lightness']" />
                <text-field-label :text="field.required ? field.invalid ? '&nbsp;' : field.validation.message || labelError : null" align="right" :color="labelErrorColor" :color-lightness="labelsColorLightness" />
              </container>
              <container v-if="field.type === 'radio-group'" :class="[`text-${field.align || 'left'}`]">
                <form-schema-label
                  :required-color="labelErrorColor"
                  :required-color-lightness="labelColorLightness"
                  :field="field"
                  :label-color="labelColor"
                  :label-color-lightness="labelColorLightness" />
                <radio-group
                  v-model="formData[field.name].value"
                  :disabled="field.disabled"
                  :vertical="field.vertical"
                  :name="field.name">
                  <radio
                    v-for="(item, index) in field.items"
                    :icon-color="color"
                    :disabled="item.disabled"
                    :icon-color-lightness="colorLightness"
                    :label-color="labelColor"
                    :label-color-lightness="labelColorLightness"
                    :value="item.value"
                    :key="index"
                    :label="item.label" />
                </radio-group>
              </container>
              <container v-if="field.type === 'chip-input'" :class="[`text-${field.align || 'left'}`]">
                <form-schema-label
                  :required-color="labelErrorColor"
                  :required-color-lightness="labelColorLightness"
                  :field="field"
                  :label-color="labelColor"
                  :label-color-lightness="labelColorLightness" />
                <chip-input
                  :color="color"
                  :color-lightness="colorLightness"
                  :label-color="titleColor"
                  :label-color-lightness="titleColorLightness"
                  :placeholder="field.placeholder"
                  :disabled="field.disabled"
                  :icon-left="field['icon-left']"
                  :icon-right="field['icon-right']"
                  :chips="field.value" />
                <text-field-label
                  align="right" :color="labelErrorColor"
                  :text="field.required ? field.invalid ? '&nbsp;' : field.validation.message || labelError : null"
                  :color-lightness="labelsColorLightness" />
              </container>
              <container v-if="field.type === 'slider'" :class="[`text-${field.align || 'left'}`]">
                <form-schema-label
                  :required-color="labelErrorColor"
                  :required-color-lightness="labelColorLightness"
                  :field="field"
                  :label-color="labelColor"
                  :label-color-lightness="labelColorLightness" />
                <slider
                  @input="onSliderInput($event, field.name)"
                  v-model="field.value"
                  :class="['mt-1']"
                  :color="color"
                  :disabled="field.disabled"
                  :min="field.min"
                  :max="field.max"
                  :step="field.step"
                  :label-color="labelColor"
                  :label-color-lightness="labelColorLightness"
                  :color-lightness="colorLightness"
                  :tick-fractions="field['tick-fractions']"
                  :height="field.height"
                  :width="field.width"
                  :vertical="field.vertical"
                  :ticks="field.ticks" />
                <container
                  :class="[`text-${field.align || 'left'}`, setTextColor('labelColor', 'labelColorLightness'), 'select-none', 'text-xs', 'mt-1']"
                  v-html="field.value" />
                <text-field-label
                  align="right"
                  :text="field.required ? field.invalid ? '&nbsp;' : field.validation.message || labelError : null"
                  :color="labelErrorColor"
                  :color-lightness="labelsColorLightness" />
              </container>
            </container>
          </flex-col>
        </flex>
      </card-text>
      <template v-if="$slots.actions">
        <slot name="actions" />
      </template>
    </card>
  </async-form>
</template>
<script>
import methods from '../../mixins/methods'
import FormSchemaLabel from './form-schema-label'

// @todo: implement otp-input, time-picker, autocomplete, time-picker-field and upload
// @todo: validation for radio buttons
// @todo: check why text-field:multiple does not validate
// @todo: add to bundler.js
// @todo: add demo code
export default {
  name: 'FormSchema',
  components: {
    FormSchemaLabel
  },
  mixins: [
    methods
  ],
  data () {
    return {
      formData: null,
      textFields: {
        'text-field': 'text',
        'password': 'password'
      },
      baseCss: this.componentBaseCss('form-schema', [])
    }
  },
  props: {
    schema: {
      type: Object,
      required: true,
      default: () => {
        return {}
      }
    },
    dense: {
      type: Boolean
    },
    rounded: {
      type: Boolean,
      default: true
    },
    title: {
      type: String
    },
    titleBgColor: {
      type: String
    },
    titleBgColorLightness: {
      type: Number
    },
    titleColor: {
      type: String
    },
    titleColorLightness: {
      type: Number
    },
    color: {
      type: String
    },
    colorLightness: {
      type: Number
    },
    labelColor: {
      type: String
    },
    labelColorLightness: {
      type: Number
    },
    bgColor: {
      type: String
    },
    bgColorLightness: {
      type: Number
    },
    labelError: {
      type: String,
      default: 'REQUIRED FIELD'
    },
    labelsColorLightness: {
      type: Number,
      default: 500
    },
    labelErrorColor: {
      type: String,
      default: 'red'
    },
    validators: {
      type: Object,
      default: () => {
        return {}
      }
    }
  },
  watch: {
    schema () {
      this.init()
    }
  },
  methods: {
    onRatingInput ($event) {
      this.formData[$event.name].value = $event.value
    },
    onSliderInput ($event, name) {
      this.formData[name].value = $event.value
    },
    onSubmit () {
      const schema = this.schema
      const formData = this.formData
      const data = {
        payload: {},
        query: null
      }
      Object.keys(formData).forEach(field => {
        data.payload[field] = formData[field].value
      })
      schema.fields.filter(field => field.required).forEach(field => {
        const name = field.name
        const formDataField = formData[name]
        const value = formDataField.value
        field.invalid = this.validators[formDataField.validation.type](value)
      })
      data.query = new URLSearchParams(data.payload).toString()
      this.$emit('submit', {
        data,
        action: schema.action,
        method: schema.method,
        enctype: schema.enctype,
        autocomplete: schema.autocomplete,
        name: schema.name,
        acceptCharset: schema['accept-charset']
      })
    },
    init () {
      const formData = {}
      this.formData = null
      this.$nextTick(() => {
        this.schema.fields.forEach(field => {
          const name = field.name
          formData[name] = {
            name,
            value: field.value,
            required: field.required,
            disabled: field.disabled,
            readonly: field.readonly,
            validation: field.validation
          }
        })
        this.formData = formData
        this.$nextTick(() => {
          this.$el.querySelectorAll('.bcq-text-field-label-required').forEach(required => {
            required.classList.add(this.setTextColor('labelErrorColor', 'labelsColorLightness'))
          })
        })
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/bcq-ui/components/form-schema/form-schema.vue